import { useMemo } from "react";
import { Button, Flex, Grid, ModalV2, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { TaxDataSourceAssignmentStatus } from "~/graphql/supergraphEnums";
import {
  useGetPositionsForCaseQuery,
  useGetTaxDataSourceAssignmentsQuery,
} from "~/graphql/supergraphHooks";
import { DOCUMENT_TYPE_MAPPING } from "../../constants";
import { POSITION_CASE_TYPES } from "../FatcaAssignmentModal/constants";
import {
  columnDefinition,
  defaultColDef,
  type FatcaPositionRow,
  gridOptions,
} from "../FatcaAssignmentModal/gridConfig";
import * as styles from "../FatcaAssignmentModal/styles_vanilla.css";
import { buildRowData } from "../FatcaAssignmentModal/utils";

interface ViewStatusModalProps {
  profileId: number;
  profileName: string;
  documentName: string;
  documentType: string;
  documentId: number;
  caseId: number;
  onClose: () => void;
}

const ViewStatusModal = ({
  profileId,
  profileName,
  documentName,
  documentType,
  documentId,
  caseId,
  onClose,
}: ViewStatusModalProps) => {
  const { data: assignmentsData, loading: assignmentsLoading } =
    useGetTaxDataSourceAssignmentsQuery({
      variables: { input: { documentId } },
    });

  const { data: positionsData, loading: positionsLoading } = useGetPositionsForCaseQuery({
    variables: {
      profileId,
      caseTypes: [...POSITION_CASE_TYPES],
    },
    fetchPolicy: "network-only",
  });

  const loading = assignmentsLoading || positionsLoading;

  const rowData = useMemo(() => {
    const assignedAssignments = (assignmentsData?.taxDataSourceAssignments ?? []).filter(
      (a) => a.status === TaxDataSourceAssignmentStatus.Assigned,
    );
    const referencedCaseIds = new Set(assignedAssignments.map((a) => a.referencedCaseId));
    const allPositions = positionsData?.complianceProfile?.positions ?? [];
    const assignedPositions = allPositions.filter(
      (p) => p.onboardingCase && referencedCaseIds.has(p.onboardingCase.id),
    );
    const allCases = positionsData?.complianceProfile?.complianceCases ?? [];
    return buildRowData(assignedPositions, allCases, profileId, caseId, {
      synthesizeCurrentCase: referencedCaseIds.has(caseId),
    });
  }, [assignmentsData, positionsData, profileId, caseId]);

  const isFatca = documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue;
  const assignmentLabel = isFatca ? "FATCA" : "CRS";

  return (
    <ModalV2
      data-testid="view-status-modal"
      show={true}
      onHide={onClose}
      title={`Positions assigned to ${assignmentLabel} data source`}
      size="large"
      closeOnClickOutside={false}
    >
      <ModalV2.Body>
        <Text className={styles.description}>
          This document was assigned as the {assignmentLabel} data source for all positions
          associated with this specific profile.
        </Text>
        <Flex className={styles.contextContainer} gap={Sizes.XL}>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">Profile</Text>
            <Text variant="regular-md">{profileName}</Text>
          </Flex>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">{assignmentLabel} data source document</Text>
            <Text variant="regular-md">{documentName}</Text>
          </Flex>
        </Flex>
        <div className={styles.gridWrapper}>
          <Grid<FatcaPositionRow>
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            columnDefs={columnDefinition}
            rowData={loading ? [] : rowData}
          />
        </div>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default ViewStatusModal;
